import type { CreateCustomer, UpdateCustomer } from '@monorepo/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/shared/lib/toast'
import { customerService } from '../services/customerService'

interface ApiError {
  response?: {
    data?: {
      error?: {
        message?: string
      }
      message?: string
    }
  }
  message?: string
}

const CUSTOMERS_QUERY_KEY = 'customers'

// Query hooks
export function useCustomers() {
  return useQuery({
    queryKey: [CUSTOMERS_QUERY_KEY],
    queryFn: () => customerService.getCustomers(),
  })
}

export function useCustomer(id: string | undefined) {
  return useQuery({
    queryKey: [CUSTOMERS_QUERY_KEY, id],
    queryFn: () => customerService.getCustomer(id!),
    enabled: !!id,
  })
}

// Mutation hooks
export function useCreateCustomer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateCustomer) => customerService.createCustomer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CUSTOMERS_QUERY_KEY] })
      toast.success('Customer created successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create customer'
      toast.error(errorMessage)
    },
  })
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCustomer }) =>
      customerService.updateCustomer(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [CUSTOMERS_QUERY_KEY] })
      queryClient.invalidateQueries({
        queryKey: [CUSTOMERS_QUERY_KEY, variables.id],
      })
      toast.success('Customer updated successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update customer'
      toast.error(errorMessage)
    },
  })
}

export function useDeleteCustomer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => customerService.deleteCustomer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CUSTOMERS_QUERY_KEY] })
      toast.success('Customer deleted successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to delete customer'
      toast.error(errorMessage)
    },
  })
}
